"use client";

//
// Convex auth for a signed-in restaurant guest.
//
// `ConvexProviderWithAuth` wants a hook shaped exactly like Clerk's `useAuth`.
// The Convex client has ONE auth slot, so this and ConvexProviderWithClerk can
// never both be mounted; src/components/convex-providers.tsx owns that split
// and asserts it in development.
//
// The JWT is fetched from /api/customer-auth/token, which reads the httpOnly
// session cookie. Nothing here can read that cookie, which is the point.

import { ConvexProviderWithAuth } from "convex/react";
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";
import { getConvexReactClient } from "@/lib/convex-react-client";

const convex = getConvexReactClient();

/** Refresh this far before `exp` so a request never rides an expiring token. */
const REFRESH_SKEW_MS = 60_000;

type CachedToken = { token: string; expiresAt: number };

type CustomerAuthState = {
  isLoading: boolean;
  isAuthenticated: boolean;
  /** Re-runs the cookie exchange, e.g. straight after a successful sign-in. */
  refresh: () => Promise<void>;
  signOut: () => Promise<void>;
};

const CustomerAuthContext = createContext<CustomerAuthState | null>(null);

export function useCustomerAuth(): CustomerAuthState {
  const value = useContext(CustomerAuthContext);
  if (!value) {
    throw new Error("useCustomerAuth must be used inside CustomerAuthProvider");
  }
  return value;
}

async function fetchCustomerToken(): Promise<CachedToken | null> {
  const response = await fetch("/api/customer-auth/token", {
    // The session cookie is httpOnly and same-site; without this the exchange
    // is anonymous and every guest looks signed out.
    credentials: "include",
    cache: "no-store",
  });

  // 401 is the ordinary "not signed in" answer. 503 means the signing key is
  // missing, which is an operator problem; both mean no identity right now.
  if (!response.ok) return null;

  const body = (await response.json()) as {
    token?: unknown;
    expiresAt?: unknown;
  };
  if (typeof body.token !== "string" || typeof body.expiresAt !== "number") {
    return null;
  }
  return { token: body.token, expiresAt: body.expiresAt };
}

export function CustomerAuthProvider({ children }: { children: ReactNode }) {
  const cache = useRef<CachedToken | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isAuthenticated, setIsAuthenticated] = useState(false);

  const getToken = useCallback(
    async ({ forceRefreshToken }: { forceRefreshToken: boolean }) => {
      const cached = cache.current;
      if (
        !forceRefreshToken &&
        cached &&
        cached.expiresAt - REFRESH_SKEW_MS > Date.now()
      ) {
        return cached.token;
      }

      let fresh: CachedToken | null = null;
      try {
        fresh = await fetchCustomerToken();
      } catch {
        // Offline or a transient failure. Treated as signed out for this
        // attempt; Convex retries, and a stale token is not served in its place.
        fresh = null;
      }

      cache.current = fresh;
      setIsAuthenticated(Boolean(fresh));
      setIsLoading(false);
      return fresh?.token ?? null;
    },
    [],
  );

  // Prime the state once so consumers do not sit in `isLoading` until Convex
  // happens to ask for a token.
  useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        const initial = await fetchCustomerToken();
        if (cancelled) return;
        cache.current = initial;
        setIsAuthenticated(Boolean(initial));
      } catch {
        if (cancelled) return;
        cache.current = null;
        setIsAuthenticated(false);
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const useAuthFromCustomerSession = useCallback(
    () => ({ isLoading, isAuthenticated, fetchAccessToken: getToken }),
    [isLoading, isAuthenticated, getToken],
  );

  const refresh = useCallback(async () => {
    cache.current = null;
    await getToken({ forceRefreshToken: true });
  }, [getToken]);

  const signOut = useCallback(async () => {
    try {
      await fetch("/api/customer-auth/logout", {
        method: "POST",
        credentials: "include",
      });
    } finally {
      cache.current = null;
      setIsAuthenticated(false);
      setIsLoading(false);
    }
  }, []);

  const value = useMemo<CustomerAuthState>(
    () => ({ isLoading, isAuthenticated, refresh, signOut }),
    [isLoading, isAuthenticated, refresh, signOut],
  );

  return (
    <CustomerAuthContext.Provider value={value}>
      <ConvexProviderWithAuth
        client={convex}
        useAuth={useAuthFromCustomerSession}
      >
        {children}
      </ConvexProviderWithAuth>
    </CustomerAuthContext.Provider>
  );
}
